import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { X, Calendar, MapPin, Users, Bell, Repeat, Video, Trash2, Edit } from "lucide-react"
import { Button } from "components/ui/button"
import { Input } from "components/ui/input"
import { Textarea } from "components/ui/textarea"
import { Label } from "components/ui/label"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog"
import { ParticipantSelector } from "./ParticipantSelector"
import { EventAttachments } from "./EventAttachments"
import type { CalendarEvent, EventType } from "../../types/agenda"
import { APPOINTMENT_TYPE_MAP } from "../../types/agenda"
import { EVENT_TYPE_CONFIG, PARTICIPANT_CHIP_COLORS } from "../../constants/agenda"
import { formatTime } from "../../utils/agenda"

interface EventDetailModalProps {
  event: CalendarEvent
  onClose: () => void
  onUpdate: (id: number, data: Partial<CalendarEvent>) => Promise<void>
  onDelete: (id: number) => Promise<void>
}

function getEventType(event: CalendarEvent): EventType {
  if (event.type && EVENT_TYPE_CONFIG[event.type]) {
    return event.type
  }
  if (event.appointment_type) {
    return APPOINTMENT_TYPE_MAP[event.appointment_type] || 'meeting'
  }
  return 'meeting'
}

function EventPanelHeader({ type, title, onClose, closeLabel = "Chiudi dettaglio evento" }: {
  type: EventType
  title: string
  onClose: () => void
  closeLabel?: string
}) {
  const config = EVENT_TYPE_CONFIG[type]
  const Icon = config.icon
  return (
    <div className="shrink-0 border-b border-[var(--hexa-border)] px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="event-type-badge inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: config.color }}>
          <Icon className="h-3 w-3 shrink-0" />
          <span>{config.label}</span>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label={closeLabel}
          className="shrink-0 text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)]">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <h2 className="mt-2 max-h-[24dvh] overflow-y-auto break-words text-base font-semibold !text-[var(--hexa-text-primary)]">{title}</h2>
    </div>
  )
}

export function EventDetailModal({ event, onClose, onUpdate, onDelete }: EventDetailModalProps) {
  const navigate = useNavigate()
  const [mode, setMode] = useState<"view" | "edit">("view")
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Form state per modalità edit
  const [title, setTitle] = useState(event.title)
  const [date, setDate] = useState(event.start_date)
  const [startTime, setStartTime] = useState(event.start_time ?? "")
  const [endTime, setEndTime] = useState(event.end_time || "")
  const [location, setLocation] = useState(event.location || "")
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<number[]>(() => {
    if (!event.participants) return []
    if (Array.isArray(event.participants)) {
      return event.participants
        .map((p: any) => typeof p === 'object' ? p.id : typeof p === 'number' ? p : null)
        .filter((id: any): id is number => id !== null)
    }
    return []
  })
  const [selectedContact, setSelectedContact] = useState<number | null>(
    (event as any).client ?? null
  )
  const [description, setDescription] = useState(event.description || "")

  const eventType = getEventType(event)
  const typeConfig = EVENT_TYPE_CONFIG[eventType]

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date)
  }

  const handleDelete = async () => {
    await onDelete(event.id)
    onClose()
  }

  // Mappa tipo frontend → backend
  const typeMapping: Record<string, string> = {
    meeting: 'MEETING',
    evento: 'EVENT',
    attivita: 'APPOINTMENT',
  }

  const handleSave = async () => {
    await onUpdate(event.id, {
      title,
      start_date: date,
      start_time: event.is_all_day ? null : startTime,
      end_time: event.is_all_day ? null : endTime,
      location,
      description,
      type: eventType,
      participants: selectedTeamMembers,
      client: selectedContact,
    } as any)

    setMode("view")
  }

  const handleCancel = () => {
    // Reset ai valori originali
    setTitle(event.title)
    setDate(event.start_date)
    setStartTime(event.start_time ?? "")
    setEndTime(event.end_time || "")
    setLocation(event.location || "")
    setDescription(event.description || "")
    setSelectedTeamMembers(() => {
      if (!event.participants || !Array.isArray(event.participants)) return []
      return event.participants
        .map((p: any) => typeof p === 'object' ? p.id : typeof p === 'number' ? p : null)
        .filter((id: any): id is number => id !== null)
    })
    setSelectedContact((event as any).client ?? null)
    setMode("view")
  }

  const capitalizeFirst = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  // Caso speciale: Task (solo visualizzazione)
  if (eventType === "task") {
    return (
      <>
        <div className="flex flex-col h-full bg-[var(--hexa-surface-elevated)]">
          {/* Header */}
          <EventPanelHeader type={eventType} title={event.title} onClose={onClose} />

          {/* Body - View only */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hexa">
            {/* Data e ora */}
            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 text-[var(--hexa-text-secondary)] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[var(--hexa-text-primary)] font-medium">
                  {capitalizeFirst(formatDate(event.start_date))}
                </div>
                <div className="text-[var(--hexa-text-secondary)] text-xs mt-1">
                  {event.start_time ? `${formatTime(event.start_time)}${event.end_time ? ` - ${formatTime(event.end_time)}` : ''}` : 'Senza orario'}
                </div>
              </div>
            </div>

            {/* Luogo */}
            {event.location && (
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-[var(--hexa-text-secondary)] flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1 text-[var(--hexa-text-primary)]">{event.location}</div>
              </div>
            )}

            {/* Partecipanti */}
            {((event as any).participants_details?.length > 0 || (event as any).client_details) && (
              <div className="flex items-start gap-3">
                <Users className="h-4 w-4 text-[var(--hexa-text-secondary)] flex-shrink-0 mt-0.5" />
                <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                  {(event as any).participants_details?.map((p: any) => (
                    <div
                      key={`team-${p.id}`}
                      className="participant-chip px-2 py-0.5 rounded-md text-[10px] font-medium text-white"
                      style={{ backgroundColor: PARTICIPANT_CHIP_COLORS.team }}
                    >
                      {p.username}
                    </div>
                  ))}
                  {(event as any).client_details && (
                    <div
                      className="participant-chip px-2 py-0.5 rounded-md text-[10px] font-medium text-white"
                      style={{ backgroundColor: PARTICIPANT_CHIP_COLORS.contact }}
                    >
                      {(event as any).client_details.first_name || (event as any).client_details.company_name || 'Cliente'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Descrizione */}
            {event.description && (
              <div className="space-y-1.5">
                <div className="text-[var(--hexa-text-secondary)] text-xs font-medium">Descrizione</div>
                <div className="text-[var(--hexa-text-primary)] whitespace-pre-wrap">{event.description}</div>
              </div>
            )}

            {/* Opzioni attive */}
            <div className="flex flex-wrap gap-2">
              {event.send_reminder && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[var(--hexa-border-subtle)] border border-[var(--hexa-border)] rounded-md text-[10px] text-[var(--hexa-text-primary)]">
                  <Bell className="h-3 w-3" />
                  Promemoria attivo
                </div>
              )}
              {event.is_recurring && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[var(--hexa-border-subtle)] border border-[var(--hexa-border)] rounded-md text-[10px] text-[var(--hexa-text-primary)]">
                  <Repeat className="h-3 w-3" />
                  Ricorrente
                </div>
              )}
              {event.add_video_link && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[var(--hexa-border-subtle)] border border-[var(--hexa-border)] rounded-md text-[10px] text-[var(--hexa-text-primary)]">
                  <Video className="h-3 w-3" />
                  Link videochiamata
                </div>
              )}
            </div>
          </div>

          {/* Footer - Solo link al modulo Task */}
          <div className="flex-shrink-0 border-t border-[var(--hexa-border)] px-4 py-3">
            <Button
              onClick={() => navigate("/tasks")}
              className="w-full text-white"
              style={{ backgroundColor: "var(--hexa-event-task)", filter: "brightness(1)" }}
              onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.1)")}
              onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
            >
              Apri nel modulo Task
            </Button>
          </div>
        </div>
      </>
    )
  }

  // Modalità VIEW per Meeting/Evento/Attività
  if (mode === "view") {
    return (
      <>
        <div className="flex flex-col h-full bg-[var(--hexa-surface-elevated)]">
          {/* Header */}
          <EventPanelHeader type={eventType} title={event.title} onClose={onClose} />

          {/* Body - View mode */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hexa">
            {/* Data e ora */}
            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 text-[var(--hexa-text-secondary)] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[var(--hexa-text-primary)] font-medium">
                  {capitalizeFirst(formatDate(event.start_date))}
                </div>
                <div className="text-[var(--hexa-text-secondary)] text-xs mt-1">
                  {event.start_time ? `${formatTime(event.start_time)}${event.end_time ? ` - ${formatTime(event.end_time)}` : ''}` : 'Senza orario'}
                </div>
              </div>
            </div>

            {/* Luogo */}
            {event.location && (
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-[var(--hexa-text-secondary)] flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1 text-[var(--hexa-text-primary)]">{event.location}</div>
              </div>
            )}

            {/* Partecipanti */}
            {((event as any).participants_details?.length > 0 || (event as any).client_details) && (
              <div className="flex items-start gap-3">
                <Users className="h-4 w-4 text-[var(--hexa-text-secondary)] flex-shrink-0 mt-0.5" />
                <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                  {(event as any).participants_details?.map((p: any) => (
                    <div
                      key={`team-${p.id}`}
                      className="participant-chip px-2 py-0.5 rounded-md text-[10px] font-medium text-white"
                      style={{ backgroundColor: PARTICIPANT_CHIP_COLORS.team }}
                    >
                      {p.username}
                    </div>
                  ))}
                  {(event as any).client_details && (
                    <div
                      className="participant-chip px-2 py-0.5 rounded-md text-[10px] font-medium text-white"
                      style={{ backgroundColor: PARTICIPANT_CHIP_COLORS.contact }}
                    >
                      {(event as any).client_details.first_name || (event as any).client_details.company_name || 'Cliente'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Descrizione */}
            {event.description && (
              <div className="space-y-1.5">
                <div className="text-[var(--hexa-text-secondary)] text-xs font-medium">Descrizione</div>
                <div className="text-[var(--hexa-text-primary)] whitespace-pre-wrap">{event.description}</div>
              </div>
            )}

            <EventAttachments key={event.id} eventId={event.id} />

            {/* Opzioni attive */}
            <div className="flex flex-wrap gap-2">
              {event.send_reminder && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[var(--hexa-border-subtle)] border border-[var(--hexa-border)] rounded-md text-[10px] text-[var(--hexa-text-primary)]">
                  <Bell className="h-3 w-3" />
                  Promemoria attivo
                </div>
              )}
              {event.is_recurring && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[var(--hexa-border-subtle)] border border-[var(--hexa-border)] rounded-md text-[10px] text-[var(--hexa-text-primary)]">
                  <Repeat className="h-3 w-3" />
                  Ricorrente
                </div>
              )}
              {event.add_video_link && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[var(--hexa-border-subtle)] border border-[var(--hexa-border)] rounded-md text-[10px] text-[var(--hexa-text-primary)]">
                  <Video className="h-3 w-3" />
                  Link videochiamata
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-[var(--hexa-border)] px-4 py-3">
            <div className="flex gap-2.5">
              {/* Modifica Mini-Card */}
              <div className="flex-1 bg-[var(--hexa-button-cancel)] rounded-lg p-2.5 flex items-center justify-center">
                <Button
                  variant="ghost"
                  onClick={() => setMode("edit")}
                  className="w-full text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)]"
                >
                  <Edit className="w-3.5 h-3.5 mr-1.5" />
                  Modifica
                </Button>
              </div>

              {/* Elimina Mini-Card */}
              <div className="flex-1 rounded-lg flex items-center justify-center">
                <Button
                  onClick={() => setShowDeleteDialog(true)}
                  className="w-full h-full bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white rounded-lg py-2.5"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Elimina
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
              <AlertDialogDescription>
                Vuoi eliminare questo evento?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>No</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>Sì</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    )
  }

  // Modalità EDIT
  return (
    <>
      <div className="flex flex-col h-full bg-[var(--hexa-surface-elevated)]">
        {/* Header */}
        <EventPanelHeader type={eventType} title={`Modifica ${typeConfig.label}`} onClose={handleCancel} closeLabel="Chiudi modifica evento" />

        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hexa">
          {/* Titolo */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">
              Titolo <span className="text-red-400">*</span>
            </Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)]"
            />
          </div>

          {/* Data */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">
              Data <span className="text-red-400">*</span>
            </Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)]"
            />
          </div>

          {/* Ora Inizio + Ora Fine */}
          <div className="grid grid-cols-2 gap-2.5">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">Ora Inizio</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">Ora Fine</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)]"
              />
            </div>
          </div>

          {/* Luogo */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">Luogo</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Es. Sala riunioni A, Google Meet, etc."
                className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] pl-10"
              />
            </div>
          </div>

          {/* Partecipanti */}
          <ParticipantSelector
            selectedTeamMembers={selectedTeamMembers}
            selectedContact={selectedContact}
            onTeamMembersChange={setSelectedTeamMembers}
            onContactChange={setSelectedContact}
          />

          {/* Descrizione */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[var(--hexa-text-secondary)]">Descrizione</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Aggiungi dettagli sull'evento..."
              rows={3}
              className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] resize-none"
            />
          </div>

          {/* Opzioni */}
          <div className="flex items-center gap-2 pt-2 border-t border-[var(--hexa-border)]">
            <span className="text-xs font-medium text-[var(--hexa-text-secondary)] mr-1">Opzioni:</span>
            {[
              { icon: Bell, label: 'Promemoria' },
              { icon: Repeat, label: 'Ricorrente' },
              { icon: Video, label: 'Videochiamata' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="relative group">
                <button
                  type="button"
                  disabled
                  className="p-2 rounded-lg bg-[var(--hexa-button-cancel)] text-[var(--hexa-text-muted)] opacity-50 cursor-not-allowed"
                >
                  <Icon className="h-4 w-4" />
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded text-[10px] bg-[var(--hexa-surface-elevated)] text-[var(--hexa-text-secondary)] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  {label} — Coming soon
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[var(--hexa-border)] px-4 py-3">
          <div className="flex gap-2.5">
            <div className="flex-1 bg-[var(--hexa-button-cancel)] rounded-lg p-2.5 flex items-center justify-center border border-[var(--hexa-border)]">
              <Button
                variant="ghost"
                onClick={handleCancel}
                className="w-full text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)]"
              >
                Annulla
              </Button>
            </div>
            <div className="flex-1 rounded-lg flex items-center justify-center">
              <Button
                onClick={handleSave}
                className="w-full h-full bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white rounded-lg py-2.5"
              >
                Salva
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi eliminare questo evento?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Sì</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
